import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { map, isEmpty, omitBy, isNil } from "lodash";
import { useHistory } from "react-router-dom";
import useSession from "Hooks/session.hook";
import moment from "moment";
import { encode } from "Helpers/index";
import {
  LoadingOutlined,
  MoreOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { DataTable, ActionMenu, Button } from "@life-line-lab/components-react";
import DataTableFilter from "Components/shared/data-table-filter";
import { BasicModal } from "@life-line-lab/components-react";
import CombineTaskModal from "../parcel-tracking-modals/combine-task-modal/combine-task-modal";
import AssignDriverModal from "../parcel-tracking-modals/assign-driver-modal/assign-driver-modal";
import { Spin, Tooltip } from "antd";

export interface Option {
  label: string;
  value: string;
}

interface PickUpListProps {
  onEdit?: any;
  onFilter?: any;
  isReadonly?: boolean;
  vehicleOptions: any;
  vehicleTypeOptions: any;
  driverOptions: any;
  customerOptions: any;
  allOrderList: any;
  loadingMechanism: any;
  onAssignDriver: (inputValue) => void;
  onCombineTask: (inputValue) => void;
  handleRefresh?: any;
}

const PickupList: React.FC<PickUpListProps> = ({
  onEdit,
  onFilter,
  isReadonly,
  vehicleOptions = [],
  vehicleTypeOptions = [],
  customerOptions,
  driverOptions,
  allOrderList,
  loadingMechanism,
  onAssignDriver,
  onCombineTask,
  handleRefresh,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const { roleTypes } = useSession();
  const initialModal: any = {
    open: false,
  };

  const initialFilter: any = {
    customersList: "",
    vehicleType: "",
    driver: "",
    unassignedDriver: "",
    unassignedVehicleType: "",
  };
  const [modal, setModal] = useState<any>(initialModal);
  const [filterState, setFilterState] = useState<{
    [key: string]: string | boolean;
  }>(initialFilter);
  const [callModal, setCallModal] = useState<any>(<></>);
  const { isLoading } = loadingMechanism;

  const handleResetModalState = () => {
    setModal(initialModal);
  };

  const [selectedRows, setSelectedRows] = useState<any>([]);
  const [pickupInfo, setPickUpInfo] = useState<any>({});

  const handleOpenPickupTrackingView = (pickupID) => {
    let encodedId = encode(pickupID);
    history.push(
      `/delivery-management/parcel-tracking/pickup/${encodedId}/view`,
    );
  };

  const handleOpenCombineTask = () => {
    setCallModal(
      <CombineTaskModal
        info={pickupInfo}
        onCancel={handleResetModalState}
        onSubmit={handleCombineTask}
      />,
    );
    setModal({ open: true });
  };

  const handleOpenAssignDriver = () => {
    pickupInfo.map((task) => {
      if (task.vehicleID) {
        setCallModal(
          <AssignDriverModal
            vehicleOptions={vehicleOptions}
            onSubmit={handleAssignDriver}
            selectedRows={selectedRows}
            info={pickupInfo}
            type="REASSIGN"
            handleCancel={handleResetModalState}
          />,
        );
        setModal({ open: true });
      } else {
        setCallModal(
          <AssignDriverModal
            vehicleOptions={vehicleOptions}
            onSubmit={handleAssignDriver}
            selectedRows={selectedRows}
            info={pickupInfo}
            type="ASSIGN"
            handleCancel={handleResetModalState}
          />,
        );
        setModal({ open: true });
      }
    });
  };

  useEffect(() => {
    if (onFilter && !isEmpty(filterState)) {
      const filterPayload = omitBy(filterState, (input) => isNil(input));
      onFilter(filterPayload);
    }
  }, [filterState]);

  const handleAssignDriver = (inputValue) => {
    onAssignDriver(inputValue);
    handleResetModalState();
  };

  const handleCombineTask = (inputValue) => {
    onCombineTask(inputValue);
    handleResetModalState();
  };

  useEffect(() => {
    getParcelInfo();
  }, [selectedRows]);

  const getParcelInfo = () => {
    let temp: any = [];
    allOrderList?.map((pickup) => {
      if (selectedRows.includes(pickup._id)) {
        temp.push(pickup);
      }
    });
    setPickUpInfo(temp);
  };

  const initialFilterSelection = [
    {
      key: "vehicle",
      name: "vehicleType",
      label: t("placeholder.filterByVehicle"),
      placeholder: t("placeholder.filterByVehicle"),
      value: filterState?.vehicleType,
      options: [{ label: t("option.all"), value: "" }].concat(
        vehicleTypeOptions.map((v) => ({
          label: v.name,
          value: v._id,
        })),
      ),
    },
    {
      key: "customer",
      name: "customersList",
      label: t("placeholder.filterCustomer"),
      placeholder: t("placeholder.filterCustomer"),
      value: filterState?.customersList,
      options: [{ label: t("option.all"), value: "" }].concat(
        customerOptions.map((org) => {
          return {
            label: org.name || "",
            value: org._id,
          };
        }),
      ),
    },
    {
      key: "driver",
      name: "driver",
      label: t("placeholder.filterByDriver"),
      placeholder: t("placeholder.filterByDriver"),
      value: filterState?.driver,
      options: [
        { label: t("option.all"), value: "" },
        { label: t("option.unassigned"), value: "UNASSIGNED" },
      ].concat(
        driverOptions.map((driver) => {
          return {
            label: driver.profile.fullName,
            value: driver.profileID,
          };
        }),
      ),
    },
  ];

  const rowSelection =
    roleTypes !== "CUSTOMER-SUPER_ADMIN"
      ? {
          onChange: (selectedRows: any[]) => {
            setSelectedRows(selectedRows);
          },
        }
      : undefined;

  const [filterSelection, setFilterSelection] = useState<any>(
    initialFilterSelection,
  );

  useEffect(() => {
    //reload the filter selection
    // if (customerOptions.length || driverOptions.length) {
    setFilterSelection(initialFilterSelection);
    // }
  }, [customerOptions, driverOptions]);

  const handleSearch = async (text: string) => {
    setFilterState((prevFilter) => ({ ...prevFilter, orFields: text }));
  };

  const handleFilterSelection = (selection) => {
    setFilterState((prevFilter) => ({
      ...prevFilter,
      customersList: selection.customersList,
      vehicleType:
        selection.vehicleType != "UNASSIGNED" ? selection.vehicleType : null,
      unassignedVehicleType: selection.vehicleType == "UNASSIGNED" && true,
      driver: selection.driver != "UNASSIGNED" ? selection.driver : null,
      unassignedDriver: selection.driver == "UNASSIGNED" && true,
    }));
  };

  const columns: any = [
    {
      title: t("label.pickUpTask").toLocaleUpperCase(),
      dataIndex: "pickupCode",
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <a className="clickable-id">{record.pickupCode || ""}</a>;
      },
      onCell: (record, rowIndex) => {
        return {
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            handleOpenPickupTrackingView(record._id);
          },
        };
      },
    },
    {
      title: t("label.dateEta").toLocaleUpperCase(),
      dataIndex: "dateEta",
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        return record.pickupDate ? (
          <span>
            {moment(record.pickupDate).format("DD-MM-YYYY") +
              " | " +
              moment(record.pickupTimeFrom).format("hh:mm a")}
          </span>
        ) : (
          <span>&#11835;</span>
        );
      },
    },
    {
      title: t("label.totalParcels").toLocaleUpperCase(),
      dataIndex: "noOfParcels",
      width: 100,
      ellipsis: true,
    },
    {
      title: t("label.cubicVolume").toLocaleUpperCase(),
      dataIndex: "totalVolume",
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{parseInt(record.totalVolume).toLocaleString()}</span>;
      },
    },
    {
      title: t("label.customer").toLocaleUpperCase(),
      dataIndex: "pickupTasks",
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <span>
            {map(record?.pickupTasks, (pickupTask) => {
              return (
                <span>
                  {pickupTask?.merchantName || <span>&#11835;</span>}
                  <br />
                </span>
              );
            })}
          </span>
        );
      },
    },
    {
      title: t("label.vehicle").toLocaleUpperCase(),
      dataIndex: "vehicleData",
      width: 105,
      ellipsis: true,
      render: (obj: any) => {
        if (!obj || !obj._id) return <span>{t("txt.unassigned")}</span>;

        return <span>{obj.name + " (" + obj.registrationNo + ")"} </span>;
      },
    },
    {
      title: t("label.locations").toLocaleUpperCase(),
      dataIndex: "totalLocations",
      width: 105,
      ellipsis: true,
    },
    {
      title: t("label.assignedDriver").toLocaleUpperCase(),
      dataIndex: "assignedDriver",
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return record.assignedDriver ? (
          <span>{record.driverData.fullName}</span>
        ) : (
          <span>{t("txt.unassigned")}</span>
        );
      },
    },
    {
      title: t("label.weight").toLocaleUpperCase(),
      dataIndex: "totalWeight",
      width: 105,
    },
  ];

  const newColumns: any = [
    {
      title: t("label.pickUpTask").toLocaleUpperCase(),
      dataIndex: "pickupCode",
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <a className="clickable-id">{record.pickupCode || ""}</a>;
      },
      onCell: (record, rowIndex) => {
        return {
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            handleOpenPickupTrackingView(record._id);
          },
        };
      },
    },
    {
      title: t("label.dateEta").toLocaleUpperCase(),
      dataIndex: "pickupDate",
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        return record.pickupDate ? (
          <span>
            {moment(record.pickupDate).format("DD-MM-YYYY") +
              " | " +
              moment(record.pickupTimeFrom).format("hh:mm a")}
          </span>
        ) : (
          <span>&#11835;</span>
        );
      },
    },
    {
      title: t("label.totalParcels").toLocaleUpperCase(),
      dataIndex: "noOfParcels",
      width: 100,
      ellipsis: true,
    },
    {
      title: t("label.cubicVolume").toLocaleUpperCase(),
      dataIndex: "totalVolume",
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{parseInt(record.totalVolume).toLocaleString()}</span>;
      },
    },
    {
      title: t("label.customer").toLocaleUpperCase(),
      dataIndex: "customers",
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <span>
            {map(record?.pickupTasks, (unique) => {
              return (
                <span>
                  {unique.merchantName || <span>&#11835;</span>}
                  <br />
                </span>
              );
            })}
          </span>
        );
      },
    },
    {
      title: t("label.vehicle").toLocaleUpperCase(),
      dataIndex: "vehicleData",
      width: 105,
      ellipsis: true,
      render: (obj: any) => {
        if (!obj || !obj._id) return <span>{t("txt.unassigned")}</span>;

        return <span>{obj.name + " (" + obj.registrationNo + ")"} </span>;
      },
    },
    {
      title: t("label.locations").toLocaleUpperCase(),
      dataIndex: "totalLocations",
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{record.pickupTasks.length}</span>;
      },
    },
    {
      title: t("label.assignedDriver").toLocaleUpperCase(),
      dataIndex: "assignedDriver",
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return record.assignedDriver ? (
          <span>{record.driverData.fullName}</span>
        ) : (
          <span>{t("txt.unassigned")}</span>
        );
      },
    },
  ];

  if (!isReadonly) {
    newColumns.push({
      title: "",
      dataIndex: "actions",
      width: 50,
      render: (_, record) => (
        <ActionMenu
          label={<MoreOutlined />}
          actions={[
            {
              label: t("action.reassignDriver"),
              // icon: <MoreOutlined />,
              onClick: () => handleActionClick(record, "reassign"),
            },
          ]}
        />
      ),
    });
  }

  const handleActionClick = (record, type) => {
    switch (type) {
      case "edit": {
        onEdit(record);
        break;
      }
      case "reassign": {
        setCallModal(
          <AssignDriverModal
            selectedRows={selectedRows}
            vehicleOptions={vehicleOptions}
            onSubmit={handleAssignDriver}
            info={pickupInfo}
            type="REASSIGN"
            handleCancel={handleResetModalState}
          />,
        );
        setModal({ open: true });
        break;
      }
    }
  };

  return (
    <div className="parcel-order-list">
      <div className="dm-header">
        <div className="filter-component">
          <DataTableFilter
            searchPlaceholder={t("placeholder.pickupSearchbar")}
            onSearch={(text: string) => {
              handleSearch(text);
            }}
            onFilter={(selection) => handleFilterSelection(selection)}
            renderFilterSelection={
              roleTypes == "CUSTOMER-SUPER_ADMIN" ? [] : filterSelection
            }
          />
          <div className="dm-buttons-container">
            <Tooltip title="Refresh" placement="left">
              <Button
                icon={isLoading ? <LoadingOutlined /> : <ReloadOutlined />}
                isDisabled={isLoading}
                onClick={() => handleRefresh()}
                shape="circle"
              />
            </Tooltip>
            {roleTypes == "COMPANY-SUPER_ADMIN" ? (
              <div className="dm-action-btn-container alignment">
                <Button
                  type="default"
                  label={t("action.combineTasks")}
                  size="large"
                  isDisabled={selectedRows.length < 2}
                  onClick={handleOpenCombineTask}
                />
                <Button
                  type="primary"
                  label={t("action.assignDriver")}
                  size="large"
                  isDisabled={isEmpty(selectedRows)}
                  onClick={handleOpenAssignDriver}
                />
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
      {isLoading ? (
        <div className="loading-container">
          <Spin size="large" indicator={<LoadingOutlined />} />
        </div>
      ) : (
        <>
          <DataTable
            rowKey={(record: any) => {
              return record._id;
            }}
            scroll={{ x: true }}
            rowSelection={rowSelection}
            columns={
              roleTypes == "CUSTOMER-SUPER_ADMIN"
                ? columns.splice(0, 4)
                : columns
            }
            dataSource={allOrderList || []}
          />
          <BasicModal
            className="modal"
            isOpen={modal.open}
            isClosable={true}
            isMaskClosable={false}
            onClose={() => {
              handleResetModalState();
            }}
          >
            {callModal}
          </BasicModal>
        </>
      )}
    </div>
  );
};

export default PickupList;
